"use client";

import { graphql, useQuery } from "@opensea/graphql";
import { ImageStack, Spinner, Toast, ToastAvatar, ToastContent, ToastErrorIcon, ToastSide, ToastSuccessIcon, ToastTitle, useToasts } from "@opensea/ui-kit";
import { omit } from "es-toolkit";
import { useEffect, useMemo } from "react";
import { useTranslations } from "use-intl";
import { useRenderImage } from "../../../providers/RenderImageProvider";
import { FlowActions } from "../../../stores/flowStore";
const FETCH_INTERVAL = 500;
const query = graphql(`
    query CheckoutToastStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
      }
    }
  `);
export function CheckoutToast({
  transactionIdentifiers,
  itemQuotes,
  itemImageMap,
  crossChain,
  isClaim,
  flow
}) {
  const t = useTranslations("wallet.CheckoutToast");
  const {
    renderImage
  } = useRenderImage();
  const {
    removeToast
  } = useToasts();
  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers: transactionIdentifiers.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash
      })),
      itemQuotes: itemQuotes.map(quote => omit(quote, ["orderId"])),
      swapProvider: crossChain ? "RELAY" : "LOCAL_BUY_NFT"
    },
    pause: true
  });
  useEffect(function pollForReceipt() {
    if (result.fetching) {
      return;
    }
    const status = result.data?.buyReceipt.status;
    if (status !== undefined && status !== "PENDING") {
      return;
    }
    if (!crossChain && transactionIdentifiers.length === 0) {
      return;
    }
    const timerId = setTimeout(() => {
      if (itemQuotes.length) {
        reexecuteQuery({
          requestPolicy: "network-only"
        });
      }
    }, FETCH_INTERVAL);
    return () => clearTimeout(timerId);
  }, [result.fetching, reexecuteQuery, result.data?.buyReceipt, itemQuotes.length, transactionIdentifiers.length, crossChain]);
  const receipt = result.data?.buyReceipt;
  useEffect(function callOnSuccessWhenComplete() {
    if (receipt?.status === "SUCCESS" && flow) {
      FlowActions.onSuccess(flow);
    }
  }, [receipt?.status, flow]);
  useEffect(function removeToastWhenComplete() {
    let timerId_0;
    if (receipt && receipt.status !== "PENDING") {
      timerId_0 = setTimeout(removeToast, receipt.status === "FAILED" ? 4000 : 1500);
    }
    return () => clearTimeout(timerId_0);
  }, [receipt, removeToast]);
  const imageUrls = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return receipt.itemReceipts.map(item_0 => item_0.item ? itemImageMap[item_0.item.id] : null);
      case "FAILED":
        return receipt.failedItemReceipts.map(item => item.item ? itemImageMap[item.item.id] : null);
      default:
        return Object.values(itemImageMap);
    }
  }, [receipt, itemImageMap]);
  const icon = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return <ToastSuccessIcon />;
      case "FAILED":
        return <ToastErrorIcon />;
      case "PENDING":
      case undefined:
        return <Spinner size="sm" />;
      default:
        return Object.values(itemImageMap);
    }
  }, [receipt?.status, itemImageMap]);
  const title = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        {
          const successKey = isClaim ? "claimTitle" : "title";
          return t(successKey, {
            count: imageUrls.length
          });
        }
      case "FAILED":
        return isClaim ? t("claimFailed") : t("failed");
      default:
        return isClaim ? t("claimPending") : t("pending");
    }
  }, [imageUrls.length, receipt?.status, isClaim, t]);
  return <Toast>
      {imageUrls.length === 1 ? <ToastAvatar renderImage={renderImage} src={imageUrls[0] || ""} /> : <ImageStack overrides={{
      Avatar: {
        position: {
          center: {
            className: "rounded",
            renderImage
          },
          right: {
            className: "rounded"
          }
        }
      }
    }} size={44} srcs={imageUrls.slice(0, 2).map(imageUrl => imageUrl || "")} />}
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
      </ToastContent>
      <ToastSide>{icon}</ToastSide>
    </Toast>;
}